import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Activity, Search, Filter, Calendar, User, Building, 
  FileText, DollarSign, Users, Shield, Clock, Eye,
  ChevronLeft, ChevronRight, Loader2, AlertTriangle,
  CheckCircle, XCircle, Edit, Trash2, Plus, Download,
  RefreshCw, Globe, MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 25;
  
  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, []);

  useEffect(() => {
    // Refetch when filters change (debounced)
    const timer = setTimeout(() => {
      setPage(0);
      fetchLogs();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, categoryFilter, actionFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/audit/stats?days=30`, { headers });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching audit stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Build query params
      const params = new URLSearchParams();
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);
      
      if (categoryFilter && categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      if (actionFilter && actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (dateFrom) {
        params.append('start_date', dateFrom);
      }
      if (dateTo) {
        params.append('end_date', dateTo);
      }
      
      const response = await fetch(`${API_URL}/api/audit/?${params.toString()}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      user: Users,
      office: Building,
      employee: User,
      payroll: DollarSign,
      salary: DollarSign,
      leave: Calendar,
      task: FileText,
      policy: Shield,
      news: FileText,
      auth: Shield,
      system: Activity,
      data: FileText,
    };
    const Icon = icons[category] || Activity;
    return <Icon className="h-4 w-4" />;
  };

  const getActionIcon = (action) => {
    const icons = {
      create: Plus,
      update: Edit,
      delete: Trash2,
      login: Shield,
      logout: Shield,
      view: Eye,
      export: Download,
      approve: CheckCircle,
      reject: XCircle,
    };
    const Icon = icons[action] || Activity;
    return <Icon className="h-3 w-3" />;
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      delete: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      login: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      logout: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      view: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
      approve: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      reject: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return mins <= 1 ? 'Just now' : `${mins} mins ago`;
    }
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    // Show date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setActionFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'user', label: 'User Management' },
    { value: 'office', label: 'Office Management' },
    { value: 'employee', label: 'Employee' },
    { value: 'payroll', label: 'Payroll' },
    { value: 'salary', label: 'Salary' },
    { value: 'leave', label: 'Leave' },
    { value: 'task', label: 'Tasks' },
    { value: 'policy', label: 'Policies' },
    { value: 'news', label: 'News' },
    { value: 'auth', label: 'Authentication' },
    { value: 'system', label: 'System' },
    { value: 'data', label: 'Data Operations' },
  ];

  const actions = [
    { value: 'all', label: 'All Actions' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'view', label: 'View' },
    { value: 'approve', label: 'Approve' },
    { value: 'reject', label: 'Reject' },
  ];

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Trail</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track and monitor all system activities and changes
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchStats(); fetchLogs(); }} data-testid="refresh-audit-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Events</p>
                <p className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.total_logs || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Sensitive Ops</p>
                <p className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.sensitive_operations || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Users</p>
                <p className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.top_users?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Period</p>
                <p className="text-2xl font-bold">
                  {statsLoading ? '...' : `${stats?.period_days || 30}d`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <Label className="sr-only">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by description, user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="audit-search-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="sr-only">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="sr-only">Action</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  {actions.map(act => (
                    <SelectItem key={act.value} value={act.value}>{act.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Activity Log</CardTitle>
          <CardDescription>
            {loading ? 'Loading...' : `Showing ${logs.length} events`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Activity className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Events Found</h3>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {searchTerm || categoryFilter !== 'all' || actionFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'System activity will appear here'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                  onClick={() => handleViewDetails(log)}
                  data-testid={`audit-log-${log.id}`}
                >
                  {/* Category Icon */}
                  <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg shrink-0">
                    {getCategoryIcon(log.category)}
                  </div>
                  
                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {log.user_name}
                      </span>
                      <Badge className={`text-xs ${getActionColor(log.action)}`}>
                        <span className="flex items-center gap-1">
                          {getActionIcon(log.action)}
                          {log.action}
                        </span>
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">
                        {log.category}
                      </Badge>
                      {log.is_sensitive && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Sensitive
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
                      {log.description}
                    </p>
                  </div>
                  
                  {/* Timestamp, IP & Location */}
                  <div className="text-right shrink-0 hidden sm:block">
                    <p className="text-sm text-gray-500">{formatTimestamp(log.timestamp)}</p>
                    {log.ip_address && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                        <Globe className="h-3 w-3" />
                        {log.ip_address}
                      </p>
                    )}
                    {log.location && log.location !== 'Unknown' && log.location !== 'Internal Network' && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                        <MapPin className="h-3 w-3" />
                        {log.location}
                      </p>
                    )}
                  </div>
                  
                  {/* View Button */}
                  <Button variant="ghost" size="sm" className="shrink-0">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500">
                Page {page + 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={logs.length < pageSize}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="audit-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Event Details
            </DialogTitle>
            <DialogDescription>
              Full details of the audit log entry
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getActionColor(selectedLog.action)}>
                    {selectedLog.action}
                  </Badge>
                  <Badge variant="outline" className="capitalize">{selectedLog.category}</Badge>
                  {selectedLog.is_sensitive && (
                    <Badge variant="destructive">Sensitive</Badge>
                  )}
                </div>
                <p className="text-gray-900 dark:text-white font-medium">
                  {selectedLog.description}
                </p>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Performed By</Label>
                  <p className="font-medium">{selectedLog.user_name}</p>
                  {selectedLog.user_phone && (
                    <p className="text-sm text-gray-500">{selectedLog.user_phone}</p>
                  )}
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Timestamp</Label>
                  <p className="font-medium">
                    {new Date(selectedLog.timestamp).toLocaleString()}
                  </p>
                </div>
                
                {selectedLog.resource_type && (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Resource Type</Label>
                    <p className="font-medium capitalize">{selectedLog.resource_type}</p>
                  </div>
                )}
                
                {selectedLog.resource_name && (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Resource Name</Label>
                    <p className="font-medium">{selectedLog.resource_name}</p>
                  </div>
                )}
                
                {selectedLog.office_name && (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Office</Label>
                    <p className="font-medium">{selectedLog.office_name}</p>
                  </div>
                )}
                
                {selectedLog.user_role && (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">User Role</Label>
                    <p className="font-medium capitalize">{selectedLog.user_role}</p>
                  </div>
                )}
                
                {selectedLog.ip_address && (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500 flex items-center gap-1"><Globe className="h-3 w-3" /> IP Address</Label>
                    <p className="font-medium font-mono text-sm">{selectedLog.ip_address}</p>
                  </div>
                )}
                
                {selectedLog.location && (
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</Label>
                    <p className="font-medium">{selectedLog.location}</p>
                  </div>
                )}
              </div>
              
              {/* Details JSON */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Additional Details</Label>
                  <pre className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Changes */}
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Changes Made</Label>
                  <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    {selectedLog.changes.before && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-500 mb-1">Before:</p>
                        <pre className="text-xs text-red-600 dark:text-red-400">
                          {JSON.stringify(selectedLog.changes.before, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.changes.after && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">After:</p>
                        <pre className="text-xs text-green-600 dark:text-green-400">
                          {JSON.stringify(selectedLog.changes.after, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Metadata */}
              <div className="pt-4 border-t text-xs text-gray-500">
                <p>Event ID: {selectedLog.id}</p>
                {selectedLog.ip_address && <p>IP Address: {selectedLog.ip_address}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditTrail;
